import { KnownOrderDtoStatus, OrderDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { NotificationsNone } from '@mui/icons-material';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import {
  COLUMN_WIDTHS,
  pinned,
  useTextColumnWidth,
  WRAP_ANYWHERE
} from '../../../../ui/components/pure/list-table/list-table-column-styles';
import { MultipleNotificationColumn } from '../../../notifications/components/multiple-notification-column';
import { orderStatusBackgroundColors } from '../../orders.constants';
import { getOrderStatusLabelForProject } from '../../../../core/util/status-label.util';
import { useHasChildProjects } from '../../../../hooks/use-has-child-projects.hook';
import { useHasMultipleHubs } from '../../../lockers/hubs.queries';
import { useBookingFlowType } from '../actions/book-order/use-booking-flow-type.hook';

const FIXED_COLUMN_WIDTHS = {
  ...COLUMN_WIDTHS,
  from: '120px',
  orderId: '90px'
} as const;

export function useOrdersTable({ projectID }: { projectID: string }) {
  const hasChildProjects = useHasChildProjects(projectID);
  const hasMultipleHubs = useHasMultipleHubs(projectID);
  const bookingFlowType = useBookingFlowType(projectID);

  const textWidth = useTextColumnWidth();
  const tb = initTable<OrderDto>();
  tb.styles({
    table: { tableLayout: 'fixed' }
  });
  tb.rowStyles({
    td: {
      height: '64px'
    }
  });

  // DESKTOP COLUMNS
  tb.column('FROM', TableComponents.TenantLogo, {
    name: ':retailer.name',
    imageURL: ':retailer.imageURL'
  }).options({
    isSortable: true,
    sortId: 'retailer.name',
    minBreakpoint: 'md',
    tooltip: 'retailer name',
    headerStyles: pinned(textWidth(FIXED_COLUMN_WIDTHS.from, 'logoOrLabel')),
    styles: WRAP_ANYWHERE
  });
  tb.column('HUB', TableComponents.TextWithAlternate, {
    value: ':hub.name',
    valueAlternate: ':bookedIntoHub.name',
    alternateTooltip: '~Booked into'
  }).options({
    isSortable: true,
    sortId: 'hub.name',
    minBreakpoint: 'md',
    tooltip: 'hub name',
    styles: WRAP_ANYWHERE
  });
  tb.column('ORDER ID', TableComponents.PrefixedText, {
    prefix: '~#',
    text: ':orderNumber'
  }).options({
    isSortable: true,
    sortId: 'orderNumber',
    minBreakpoint: 'md',
    tooltip: 'order number',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.orderId),
    styles: WRAP_ANYWHERE
  });
  tb.column('TO', TableComponents.BoldText, {
    value: ':customer.displayName'
  }).options({
    isSortable: true,
    sortId: 'customer.displayName',
    minBreakpoint: 'md',
    tooltip: 'customer full name',
    styles: WRAP_ANYWHERE
  });
  tb.column('GROUP', TableComponents.Text, {
    value: ':customerGroup.name'
  }).options({
    isSortable: true,
    sortId: 'customerGroup.name',
    minBreakpoint: 'md',
    tooltip: 'customer group name'
  });
  tb.column('BOOKING TIME', TableComponents.DateTime, {
    value: ':bookingTime'
  }).options({
    isSortable: true,
    sortId: 'bookingTime',
    minBreakpoint: 'md',
    tooltip: 'booking time',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.dateTime)
  });
  tb.column('DELIVERED TIME', TableComponents.DateTime, {
    value: ':deliveredTime'
  }).options({
    isSortable: true,
    sortId: 'deliveredTime',
    minBreakpoint: 'md',
    tooltip: 'delivered time',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.dateTime)
  });
  tb.column('COLLECTED TIME', TableComponents.DateTime, {
    value: ':collectedTime'
  }).options({
    isSortable: true,
    sortId: 'collectedTime',
    minBreakpoint: 'md',
    tooltip: 'collected time',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.dateTime)
  });
  tb.column('STATUS', TableComponents.WorkflowStatus, {
    label: (row) => {
      return getOrderStatusLabelForProject(row.status as KnownOrderDtoStatus, bookingFlowType);
    },
    color: (row) => orderStatusBackgroundColors[row.status as KnownOrderDtoStatus]
  }).options({
    isSortable: true,
    sortId: 'status',
    minBreakpoint: 'md',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.status),
    tooltip: 'order status'
  });
  tb.column(
    {
      name: 'NOTIFICATIONS',
      render: () => (
        <div>
          <NotificationsNone />
        </div>
      )
    },
    MultipleNotificationColumn,
    {
      notifications(row) {
        return row.notifications;
      },
      align: '~left',
      iconSize: `~24px`,
      maxVisibleNotifications: '~1'
    }
  ).options({
    minBreakpoint: 'md',
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.notifications)
  });

  // MOBILE COLUMNS
  tb.columnGroup('BOOKING TIME')
    .row(TableComponents.DateTime, { value: ':bookingTime' })
    .row(TableComponents.BoldText, { value: ':customer.displayName' })
    .row(TableComponents.Text, { value: ':customerGroup.name' })
    .row(TableComponents.PrefixedText, { text: ':orderNumber', prefix: '~#' })
    .options({
      isSortable: true,
      sortId: 'bookingTime',
      maxBreakpoint: 'sm',
      tooltip: 'booking time'
    });

  tb.columnGroup('')
    .row(TableComponents.WorkflowStatus, {
      label: (row) => {
        return getOrderStatusLabelForProject(row.status as KnownOrderDtoStatus, bookingFlowType);
      },
      color: (row) => orderStatusBackgroundColors[row.status as KnownOrderDtoStatus],
      align: '~center'
    })
    .row(
      TableComponents.TextWithAlternate,
      {
        value: ':hub.name',
        valueAlternate: ':bookedIntoHub.name',
        alternateTooltip: '~Booked into'
      },
      // If there is more than one hub, show the hub name in the table
      () => hasMultipleHubs
    )
    .row(
      TableComponents.TenantLogo,
      {
        name: ':retailer.name',
        imageURL: ':retailer.imageURL',
        align: '~right'
      },
      // If there are child projects or only one hub, show the project name in the table
      () => hasChildProjects || !hasMultipleHubs
    )
    .options({
      isSortable: false,
      maxBreakpoint: 'sm',
      styles: { display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-end' }
    });
  return tb;
}
